import React, { useState } from 'react';
import { Eye, Edit2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import { Customer, ProjectRequestListItem } from '../../../types';
import LoadingSpinner from '../../../components/LoadingSpinner';
import tableStyles from '../../../components/table/tableStyles';
import SortableHeaderCell from '../../../components/table/SortableHeaderCell';
import DragOverlayHeader from '../../../components/table/DragOverlayHeader';
import ColumnVisibilityToggle from '../../../components/table/ColumnVisibilityToggle';
import { getRandomBorderColor } from "../../../utils/randomColor";

// ── Column definitions ────────────────────────────────────────────────────────
const INITIAL_COLUMNS = [
  { id: 'project',      label: 'Project' },
  { id: 'customer',     label: 'Customer' },
  { id: 'submittedBy',  label: 'Submitted By' },
  { id: 'funding',      label: 'Funding' },
  { id: 'workType',     label: 'Work Type' },
  { id: 'workRequest',  label: 'Work Request' },
  { id: 'parcels',      label: 'Impacted Parcels' },
  { id: 'research',     label: 'Research - Govt Owned' },
  { id: 'acquisition',  label: 'Acquisition - Govt Owned' },
  { id: 'qaqc',         label: 'QA/QC - Has Docs' },
  { id: 'deal',         label: 'Deal' },
  { id: 'created',      label: 'Created' },
  { id: 'updated',      label: 'Updated' },
  { id: 'convertDeal',  label: 'Convert Deal' },
  { id: 'actions',      label: 'Actions' },
] as const;

type ColumnId = typeof INITIAL_COLUMNS[number]['id'];
type Column = { id: ColumnId; label: string };

// ── Props ─────────────────────────────────────────────────────────────────────
interface ProjectRequestTableProps {
  currentRequests: ProjectRequestListItem[];
  customers: Customer[];
  highlightedId: number | null;
  converting: number | null;
  loading: boolean;
  searchQuery: string;
  renderBooleanCell: (value: boolean | null | undefined) => React.ReactNode;
  renderWorkRequestBadges: (workRequest: string[] | null | undefined) => React.ReactNode;
  onConvert: (requestId: number) => void;
  onView: (requestId: number) => void;
  onEdit: (requestId: number) => void;
}

// ── Cell renderer map ─────────────────────────────────────────────────────────
// Returns the <td> content for a given column id and row.
function renderCell(
  colId: ColumnId,
  r: ProjectRequestListItem,
  props: ProjectRequestTableProps,
  isHighlighted: boolean,
): React.ReactNode {
  const { customers, converting, renderBooleanCell, renderWorkRequestBadges, onConvert, onView, onEdit } = props;

  switch (colId) {
    case 'project':
      return isHighlighted ? (
        <span className="inline-flex items-center gap-1 text-green-700 font-medium text-sm">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          {r.project_name}
        </span>
      ) : (
        <span className="text-sm theme-text-primary">{r.project_name}</span>
      );

    case 'customer':
      return r.customer_name || customers.find((c) => c.id === r.customer_id)?.item || '-';

    case 'submittedBy':
      return r.submitted_by ? r.submitter.name : '-';

    case 'funding':
      return r.funding_type || '-';

    case 'workType':
      return r.work_type || '-';

    case 'workRequest':
      return renderWorkRequestBadges(r.work_request);

    case 'parcels':
      return r.impacted_parcels !== null && r.impacted_parcels !== undefined ? r.impacted_parcels : '-';

    case 'research':
      return renderBooleanCell(r.research_is_government_owned);

    case 'acquisition':
      return renderBooleanCell(r.acquisition_is_government_owned);

    case 'qaqc':
      return renderBooleanCell(r.qa_has_documentation);

    case 'deal':
      return r.deal ? (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium theme-text-primary">{r.deal.name}</span>
          <span className="text-sm theme-text-tertiary">{r.deal.stage}</span>
        </div>
      ) : (
        <span className="theme-text-tertiary">-</span>
      );

    case 'created':
      return new Date(r.created_at).toLocaleString();

    case 'updated':
      return new Date(r.updated_at).toLocaleString();

    case 'convertDeal':
      return !r.deal ? (
        <button
          className="px-2 py-1 rounded theme-border theme-bg-primary hover:opacity-80 disabled:opacity-50 text-sm theme-text-primary"
          disabled={converting === r.id}
          onClick={() => onConvert(r.id)}
        >
          {converting === r.id ? 'Converting...' : 'Convert to Deal'}
        </button>
      ) : null;

    case 'actions':
      return (
        <div className="flex items-center gap-1.5">
          <button
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
            onClick={() => onView(r.id)}
            title="View details"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          {!r.deal && (
            <button
              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
              onClick={() => onEdit(r.id)}
              title="Edit project request"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      );

    default:
      return '-';
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
const ProjectRequestTable: React.FC<ProjectRequestTableProps> = (props) => {

  /*randome color*/
  const [color] = useState(getRandomBorderColor());

  const { currentRequests, highlightedId, loading, searchQuery } = props;

  const [columns, setColumns] = useState<Column[]>([...INITIAL_COLUMNS]);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const visibleColumns = columns.filter((c) => !hiddenColumns.has(c.id));
  const activeColumn = columns.find((c) => c.id === activeId);

  const handleDragStart = ({ active }: DragStartEvent) => setActiveId(String(active.id));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setColumns((prev) => {
      const oldIdx = prev.findIndex((c) => c.id === active.id);
      const newIdx = prev.findIndex((c) => c.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handleDragCancel = () => setActiveId(null);

  const toggleColumn = (id: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <ColumnVisibilityToggle columns={columns} hiddenColumns={hiddenColumns} onToggle={toggleColumn} />
      </div>

      <div style={tableStyles.wrapper}>
      <table
          style={{
            ...tableStyles.table,
            fontSize: 13,
            "--row-border": color,
          } as React.CSSProperties}
        >
          {/* Draggable thead */}
          <thead style={tableStyles.thead}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={visibleColumns.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                <tr className='border-l-[5px] border-[var(--row-border)]'>
                  {visibleColumns.map((col) => (
                    <SortableHeaderCell key={col.id} column={col} />
                  ))}
                </tr>
              </SortableContext>

              <DragOverlay dropAnimation={{ duration: 180, easing: 'ease' }}>
                {activeColumn ? <DragOverlayHeader column={activeColumn} /> : null}
              </DragOverlay>
            </DndContext>
          </thead>

          <tbody>
            {currentRequests.map((r) => {
              const isHighlighted = highlightedId === r.id;
              return (
                <tr
                  key={r.id}
                  data-project-id={r.id}
                  className={`theme-border-t transition-all duration-1000 border-l-[5px] border-[var(--row-border)] ${
                    isHighlighted ? 'bg-green-50 border-green-200 shadow-md' : 'theme-table-row'
                  }`}
                >
                  {visibleColumns.map((col) => (
                    <td key={col.id} className="px-2 py-1.5 text-sm theme-text-secondary">
                      {renderCell(col.id as ColumnId, r, props, isHighlighted)}
                    </td>
                  ))}
                </tr>
              );
            })}

            {loading && currentRequests.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="p-0">
                  <div className="p-4">
                    <LoadingSpinner message="Loading project requests..." />
                  </div>
                </td>
              </tr>
            ) : !currentRequests.length ? (
              <tr>
                <td className="px-2 py-3 text-sm theme-text-tertiary" colSpan={visibleColumns.length}>
                  {searchQuery ? 'No project requests found matching your search' : 'No project requests'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectRequestTable;
